import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface JobIntelAccess {
  has_subscription: boolean;
  is_active: boolean;
  subscription: {
    id: string;
    status: string;
    plan_key: string;
    billing_cycle: string;
    trial_end_date: string | null;
    current_period_end: string | null;
  } | null;
  plan: {
    id: string;
    plan_key: string;
    plan_name: string;
    monthly_credits: number | null;
    is_unlimited: boolean;
    support_level: string;
  } | null;
  credits_remaining: number;
  credits_used_this_period: number;
  is_unlimited: boolean;
  permissions: {
    express_reports: boolean;
    snapshot_reports: boolean;
    deep_dive_reports: boolean;
    job_hub: boolean;
    tailorwiz_integration: boolean;
    inner_circle_full: boolean;
    career_clarity_full: boolean;
    api_access: boolean;
    white_label: boolean;
    shareable_links: boolean;
  };
}

interface CreditCheckResult {
  success: boolean;
  error_code?: string;
  message?: string;
  transaction_id?: string;
  credits_charged?: number;
  credits_remaining?: number | null;
  is_unlimited?: boolean;
  current_balance?: number;
  cost?: number;
}

interface RefundResult {
  success: boolean;
  error_code?: string;
  message?: string;
  credits_refunded?: number;
  new_balance?: number | null;
}

export function useJobIntelCredits() {
  const { user } = useAuth();
  const [access, setAccess] = useState<JobIntelAccess | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAccess = useCallback(async () => {
    if (!user) {
      setAccess(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('get-user-jobintel-access', {
        body: { user_id: user.id }
      });

      if (error) {
        console.error('Failed to fetch JobIntel access:', error);
        setAccess(null);
      } else {
        setAccess(data as JobIntelAccess);
      }
    } catch (err) {
      console.error('Error fetching JobIntel access:', err);
      setAccess(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchAccess();
  }, [fetchAccess]);

  const checkAndDeductCredits = async (
    reportType: string,
    reportVersion: string
  ): Promise<CreditCheckResult> => {
    if (!user) {
      return { success: false, error_code: 'NOT_AUTHENTICATED', message: 'Please sign in' };
    }

    const { data, error } = await supabase.functions.invoke('check-and-deduct-credits', {
      body: { user_id: user.id, report_type: reportType, report_version: reportVersion }
    });

    if (error) {
      return { success: false, error_code: 'NETWORK_ERROR', message: 'Failed to connect to server' };
    }

    // Refresh access data after deduction
    if (data.success) {
      await fetchAccess();
    }

    return data as CreditCheckResult;
  };

  const refundCredits = async (transactionId: string): Promise<RefundResult> => {
    if (!user) {
      return { success: false, error_code: 'NOT_AUTHENTICATED', message: 'Please sign in' };
    }

    const { data, error } = await supabase.functions.invoke('refund-credits', {
      body: { user_id: user.id, transaction_id: transactionId }
    });

    if (error) {
      return { success: false, error_code: 'NETWORK_ERROR', message: 'Failed to connect to server' };
    }

    // Refresh access data after refund
    if (data.success) {
      await fetchAccess();
    }

    return data as RefundResult;
  };

  return {
    access,
    loading,
    refreshAccess: fetchAccess,
    checkAndDeductCredits,
    refundCredits,
  };
}
